// Shared canonicalization for RadioStateModel.
// Import/read paths can assemble partially-valid state from JSON or receiver bytes,
// then pass it through this module to produce the single runtime shape that the
// rest of the app should reason about.
import { sanitizeScanDwellTime, sanitizeTelemetryPeakTime } from "./radio-sanitizers";
import { VISIBLE_SCAN_LIST_COUNT, getDefaultVisibleScanListName } from "./radio-layout";
import {
  createDefaultRadioState,
  type ForceBandwidthMode,
  type RadioStateModel
} from "../types/radio-types";

const TELEMETRY_RX_GAIN_MIN = 0;
const TELEMETRY_RX_GAIN_MAX = 24;
const TELEMETRY_PULSE_THRESHOLD_DBM_MIN = -125;
const TELEMETRY_PULSE_THRESHOLD_DBM_MAX = -60;
const TELEMETRY_FULL_SCALE_DBM_MIN = -100;
const TELEMETRY_FULL_SCALE_DBM_MAX = -20;
const TELEMETRY_PULSE_THRESHOLD_DBM_DEFAULT = -95;
const TELEMETRY_FULL_SCALE_DBM_DEFAULT = -31;
const FREQUENCY_OFFSET_HZ_MIN = -5000;
const FREQUENCY_OFFSET_HZ_MAX = 5000;
const BATTERY_CALIBRATION_MIN = 1500;
const BATTERY_CALIBRATION_MAX = 3500;

export interface RadioNormalizationResult {
  radio: RadioStateModel;
  adjustedCount: number;
}

// Squelch is a runtime UI value, so normalize it here rather than in transport code.
function sanitizeSquelchLevel(value: number): number {
  if (!Number.isFinite(value)) {
    return createDefaultRadioState().activeOptions.receiver.squelch_level;
  }
  return Math.max(0, Math.min(9, Math.round(value)));
}

function sanitizeBacklightTime(value: number): number {
  if (!Number.isFinite(value)) {
    return createDefaultRadioState().activeOptions.display.backlight_time;
  }
  return Math.max(0, Math.min(7, Math.round(value)));
}

function sanitizeScanResumeMode(value: string): RadioStateModel["activeOptions"]["scan"]["resume_mode"] {
  return value === "stop" || value === "dwell"
    ? value
    : createDefaultRadioState().activeOptions.scan.resume_mode;
}

function sanitizeNavigationMode(value: string): RadioStateModel["activeOptions"]["receiver"]["navigation_mode"] {
  return value === "channel" || value === "id"
    ? value
    : createDefaultRadioState().activeOptions.receiver.navigation_mode;
}

function sanitizePowerOnDisplayMode(
  value: string
): RadioStateModel["activeOptions"]["display"]["power_on_display_mode"] {
  switch (value) {
    case "message":
    case "sound":
    case "voltage":
    case "logo":
    case "none":
      return value;
    default:
      return createDefaultRadioState().activeOptions.display.power_on_display_mode;
  }
}

function sanitizeArrowOrientation(
  value: string
): RadioStateModel["activeOptions"]["keys"]["arrow_orientation"] {
  return value === "up_down" || value === "left_right"
    ? value
    : createDefaultRadioState().activeOptions.keys.arrow_orientation;
}

// These telemetry-facing sanitizers are exported because receiver write paths still
// need them when encoding canonical state into device bytes.
export function sanitizeTelemetryPulseThresholdDbm(value: number): number {
  if (!Number.isFinite(value)) {
    return TELEMETRY_PULSE_THRESHOLD_DBM_DEFAULT;
  }
  return Math.max(
    TELEMETRY_PULSE_THRESHOLD_DBM_MIN,
    Math.min(TELEMETRY_PULSE_THRESHOLD_DBM_MAX, Math.round(value))
  );
}

export function sanitizeTelemetryFullScaleDbm(value: number): number {
  if (!Number.isFinite(value)) {
    return TELEMETRY_FULL_SCALE_DBM_DEFAULT;
  }
  return Math.max(
    TELEMETRY_FULL_SCALE_DBM_MIN,
    Math.min(TELEMETRY_FULL_SCALE_DBM_MAX, Math.round(value))
  );
}

export function sanitizeBatteryCalibration(value: number): number {
  if (!Number.isFinite(value)) {
    return 2300;
  }
  return Math.max(BATTERY_CALIBRATION_MIN, Math.min(BATTERY_CALIBRATION_MAX, Math.round(value)));
}

export function sanitizeFrequencyOffsetHz(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  let rounded = Math.round(value);
  if (rounded < FREQUENCY_OFFSET_HZ_MIN || rounded > FREQUENCY_OFFSET_HZ_MAX) {
    return 0;
  }

  // Match firmware's integer-arithmetic behavior exactly:
  // positive values use ((x + 5) / 10) * 10 and negatives use
  // ((x - 5) / 10) * 10, where division truncates toward zero.
  if (rounded >= 0) {
    rounded = Math.trunc((rounded + 5) / 10) * 10;
  } else {
    rounded = Math.trunc((rounded - 5) / 10) * 10;
  }

  if (rounded < FREQUENCY_OFFSET_HZ_MIN || rounded > FREQUENCY_OFFSET_HZ_MAX) {
    return 0;
  }

  return rounded;
}

export function sanitizeTelemetryRxGain(value: number): number {
  if (!Number.isFinite(value)) {
    return 12;
  }
  return Math.max(TELEMETRY_RX_GAIN_MIN, Math.min(TELEMETRY_RX_GAIN_MAX, Math.round(value)));
}

export function sanitizeTelemetryForceBandwidth(value: string): ForceBandwidthMode {
  switch (value) {
    case "dont_force":
    case "wide":
    case "narrow":
    case "narrower":
      return value;
    default:
      return "dont_force";
  }
}

// Scan-list names have a user-visible defaulting rule that needs to be shared by
// JSON import/export and receiver read/write behavior.
function sanitizeScanListName(name: string, index: number): string {
  return (
    name
      .replace(/[^\x20-\x7E]/g, "")
      .slice(0, 10)
      .trim() || getDefaultVisibleScanListName(index)
  );
}

function normalizeScanListNames(names: string[]): { names: string[]; adjusted: number } {
  const defaults = createDefaultRadioState().activeOptions.scan.list_names;
  const normalized = [...defaults];
  let adjusted = 0;

  for (let i = 0; i < Math.min(VISIBLE_SCAN_LIST_COUNT, names.length); i += 1) {
    const sanitized = sanitizeScanListName(names[i] ?? "", i);
    normalized[i] = sanitized;
    if (sanitized !== names[i]) {
      adjusted += 1;
    }
  }

  if (names.length > VISIBLE_SCAN_LIST_COUNT) {
    adjusted += names.length - VISIBLE_SCAN_LIST_COUNT;
  }

  return { names: normalized, adjusted };
}

// Canonicalize one RadioStateModel in a single pass. This is the "one place"
// where app-level radio invariants should be enforced before downstream code
// uses the state or before output paths serialize it.
export function normalizeRadio(input: RadioStateModel): RadioNormalizationResult {
  let adjustedCount = 0;

  const batteryType = Math.max(0, Math.min(4, Math.round(input.activeOptions.battery_type)));
  if (batteryType !== input.activeOptions.battery_type) {
    adjustedCount += 1;
  }

  const activeListMask = input.activeOptions.scan.active_list_mask & 0xffff;
  if (activeListMask !== input.activeOptions.scan.active_list_mask) {
    adjustedCount += 1;
  }

  const normalizedListNames = normalizeScanListNames(input.activeOptions.scan.list_names);
  adjustedCount += normalizedListNames.adjusted;

  const resumeMode = sanitizeScanResumeMode(input.activeOptions.scan.resume_mode);
  if (resumeMode !== input.activeOptions.scan.resume_mode) {
    adjustedCount += 1;
  }

  const dwellTimeSeconds = sanitizeScanDwellTime(input.activeOptions.scan.dwell_time_seconds);
  if (dwellTimeSeconds !== input.activeOptions.scan.dwell_time_seconds) {
    adjustedCount += 1;
  }

  const peakTime = sanitizeTelemetryPeakTime(input.activeOptions.telemetry.peak_time);
  if (peakTime !== input.activeOptions.telemetry.peak_time) {
    adjustedCount += 1;
  }

  const rfGain = sanitizeTelemetryRxGain(input.activeOptions.receiver.rf_gain);
  if (rfGain !== input.activeOptions.receiver.rf_gain) {
    adjustedCount += 1;
  }

  const squelchLevel = sanitizeSquelchLevel(input.activeOptions.receiver.squelch_level);
  if (squelchLevel !== input.activeOptions.receiver.squelch_level) {
    adjustedCount += 1;
  }

  const navigationMode = sanitizeNavigationMode(input.activeOptions.receiver.navigation_mode);
  if (navigationMode !== input.activeOptions.receiver.navigation_mode) {
    adjustedCount += 1;
  }

  const powerOnDisplayMode = sanitizePowerOnDisplayMode(
    input.activeOptions.display.power_on_display_mode
  );
  if (powerOnDisplayMode !== input.activeOptions.display.power_on_display_mode) {
    adjustedCount += 1;
  }

  const backlightTime = sanitizeBacklightTime(input.activeOptions.display.backlight_time);
  if (backlightTime !== input.activeOptions.display.backlight_time) {
    adjustedCount += 1;
  }

  const bootLine1 = input.activeOptions.boot.line1.slice(0, 15);
  if (bootLine1 !== input.activeOptions.boot.line1) {
    adjustedCount += 1;
  }

  const bootLine2 = input.activeOptions.boot.line2.slice(0, 15);
  if (bootLine2 !== input.activeOptions.boot.line2) {
    adjustedCount += 1;
  }

  const arrowOrientation = sanitizeArrowOrientation(input.activeOptions.keys.arrow_orientation);
  if (arrowOrientation !== input.activeOptions.keys.arrow_orientation) {
    adjustedCount += 1;
  }

  const pulseThresholdDbm = sanitizeTelemetryPulseThresholdDbm(
    input.activeOptions.developer.pulse_threshold_dbm
  );
  if (pulseThresholdDbm !== input.activeOptions.developer.pulse_threshold_dbm) {
    adjustedCount += 1;
  }

  const fullScaleDbm = sanitizeTelemetryFullScaleDbm(input.activeOptions.developer.full_scale_dbm);
  if (fullScaleDbm !== input.activeOptions.developer.full_scale_dbm) {
    adjustedCount += 1;
  }

  const frequencyOffsetHz = sanitizeFrequencyOffsetHz(
    input.activeOptions.developer.frequency_offset_hz
  );
  if (frequencyOffsetHz !== input.activeOptions.developer.frequency_offset_hz) {
    adjustedCount += 1;
  }

  const forceBandwidth = sanitizeTelemetryForceBandwidth(input.activeOptions.developer.force_bandwidth);
  if (forceBandwidth !== input.activeOptions.developer.force_bandwidth) {
    adjustedCount += 1;
  }

  const batteryCalibration = sanitizeBatteryCalibration(
    input.activeOptions.developer.battery_calibration
  );
  if (batteryCalibration !== input.activeOptions.developer.battery_calibration) {
    adjustedCount += 1;
  }

  if (adjustedCount === 0) {
    return { radio: input, adjustedCount: 0 };
  }

  return {
    radio: {
      ...input,
      activeOptions: {
        ...input.activeOptions,
        battery_type: batteryType,
        scan: {
          ...input.activeOptions.scan,
          active_list_mask: activeListMask,
          list_names: normalizedListNames.names,
          resume_mode: resumeMode,
          dwell_time_seconds: dwellTimeSeconds
        },
        telemetry: {
          ...input.activeOptions.telemetry,
          peak_time: peakTime
        },
        receiver: {
          ...input.activeOptions.receiver,
          squelch_level: squelchLevel,
          navigation_mode: navigationMode,
          rf_gain: rfGain
        },
        display: {
          ...input.activeOptions.display,
          power_on_display_mode: powerOnDisplayMode,
          backlight_time: backlightTime
        },
        boot: {
          line1: bootLine1,
          line2: bootLine2
        },
        keys: {
          ...input.activeOptions.keys,
          arrow_orientation: arrowOrientation
        },
        developer: {
          ...input.activeOptions.developer,
          pulse_threshold_dbm: pulseThresholdDbm,
          full_scale_dbm: fullScaleDbm,
          frequency_offset_hz: frequencyOffsetHz,
          force_bandwidth: forceBandwidth,
          battery_calibration: batteryCalibration
        }
      }
    },
    adjustedCount
  };
}
